import { ChangeDetectorRef, ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { FoodDbService, MealNameSuggestion, MealTunerSource } from 'services/food-db.service';
import { UserDb } from 'services/user-db.service';
import { MealTuner } from '@cf-shared/meal-tuner';
import { DateTime } from 'luxon';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { NutrientBarStyle, NutrientDisplayMode } from 'components/nutrient-list/nutrient-list';
import { DatabaseWrapper, DayUpdatedEvent, DocWatcher } from 'services/database-wrapper';
import { AuthService } from 'services/auth.service';
import { IMealSuggestionsPage, MealSuggestionsPageParams, MealSuggestionsMode } from 'pages/meal-suggestions/meal-suggestions-interface';
import { SettingsService } from 'services/settings.service';
import { DebugService } from 'services/debug.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { ToastOptions, ToastType } from 'components/toast/toast-host';
import { AuthRole } from '@cf-shared/couch-auth';
import { MealTunerMeal, MealTunerOutcome } from '@cf-shared/meal-tuner-interface';
import { TelemetryService } from 'services/telemetry.service';
import { MealActionsService } from 'services/meal-actions.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { IInputCalendarPage } from 'pages/input-calendar-page/input-calendar-interface';
import { INutritionInfoPage } from 'pages/nutrition-info-page/nutrition-info-interface';
import { LoggingService } from 'services/logging.service';
import { FoodReadFailedError, FoodReadFailurePolicy } from 'services/food-read-error';
import { showFoodLoadFailedAlertAsync } from 'services/food-load-failed-alert';
import { FormatNutrients, formatTime, formatRelativeDay, formatScheduledMealName } from 'util/formatting';
import { IngredientList } from 'components/ingredient-search/ingredient-list';
import { marked } from 'marked';
import { PubSubService } from 'services/pubsub.service';
import { PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { ComplexFood, isLogMeal, AnyFood, isLogMealById, isComplexFood, Nutrients, NutrientTargets, calculateNutrientMatch_pc } from '@cf-shared/data-model/food';
import { tunableTargetsOr } from '@cf-shared/meal-targets-as-if-empty';
import { isIngredientReal, Ingredient, IngredientReal, IngredientType, ingredientFoodSetsMatch } from '@cf-shared/data-model/ingredient';
import { CannotTuneReason, classifyCannotTune } from 'util/cannot-tune-reason';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { deepClone } from '@cf-shared/deep-helpers';
import { Recipe, isRecipeById, isRecipe } from '@cf-shared/data-model/recipe';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordState, LogRecordType } from '@cf-shared/data-model/user-log';
import { EditIngredientResult } from 'pages/edit-ingredient/edit-ingredient-interface';
import { IngredientEditReturnContext } from 'components/ingredient-search/ingredient-add-telemetry';
import { FoodsPageResult, IFoodsPage } from 'pages/foods/foods-interface';
import { ErrandAnswerShape } from 'pages/foods/add-food-errand';
import { ILabelScannerPage } from 'pages/label-scanner-page/label-scanner-interface';

import { EditMealPageParams, EditMealScope } from './edit-meal-interface';
import { mergeCopiedMealIngredients } from './copy-meal-ingredients';
import { markerAfterAdding } from 'util/meal-source-marker';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { orderRoutesByUsage, readRouteUsage, recordRouteUse, RouteUsageScores } from './add-route-usage';
import { NutritionTab } from 'pages/nutrition/nutrition-interface';
import { IMealPhotoScannerPage, MealPhotoScannerResult, mealPhotoScanMealParams } from 'pages/meal-photo-scanner-page/meal-photo-scanner-interface';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';

/**
 * How long the Auto-Tune confirmation stays on screen, matching the food-added one.
 *
 * Short is right here because this bar only ever reports the GOOD outcome — the meal hit its
 * targets. There is nothing to weigh up and nothing to read twice, and the Undo beside it is
 * for the user who changes their mind straight away, not for one who needs to think. The
 * outcome that DOES need thinking about is an alert instead of a bar (owner decision,
 * 2026-09-04); see `showAutoTuneResultAsync`.
 */
const AUTO_TUNE_TOAST_DURATION_MS = 5000;

/**
 * How close a meal has to be to its targets before the app calls it "on target".
 *
 * The same number appears elsewhere for other questions — a day's card colour, a meal row's
 * colour — and those are deliberately left alone here; this one is Auto-Tune's own verdict.
 */
const ON_TARGET_MATCH_PC = 95;

/**
 * How much better the match has to get before rewriting an already-on-target meal is worth it.
 *
 * Not zero, for two reasons. The obvious one: a gain of a tenth of a percent is invisible to the
 * user and does not justify moving every amount beside their food.
 *
 * The one that actually bites: the two numbers being compared are computed by different code.
 * The "before" figure is measured from the meal's stored nutrients, which come from the food
 * database; the tuner scores its own proposal from its own arithmetic over the same foods. They
 * agree closely but not exactly, and the difference has a direction — so comparing them for any
 * improvement at all made the tuner look very slightly better than standing still EVERY time.
 * The meal was then rewritten on every press, for ever, which is what a user reported (#5297).
 */
const MEANINGFUL_MATCH_GAIN_PC = 1;

/**
 * What splitting the meal's recipes into their parts achieved — see `splitRecipesAsync`.
 *
 * The split SAVES the meal, so "did it change the list in memory" and "is the change real" are
 * two different questions, and only the second one tells Auto-Tune whether there is anything to
 * put back. They used to be answered by a single boolean that only knew the first (#5297).
 */
enum RecipeSplitOutcome {
  /** Recipes were replaced by their parts, and the meal was saved that way. */
  Split = 'split',
  /**
   * Nothing needed splitting, or every split came back empty. The meal is exactly as it was, so
   * there is nothing for Undo to reverse.
   */
  NothingToSplit = 'nothing-to-split',
  /**
   * The meal could not be saved. `updateMealAsync` has already opened the food-load alert, so the
   * user has been told; the caller's job is to stop rather than to say it again.
   */
  SaveFailed = 'save-failed',
}

/**
 * Everything Auto-Tune can change about a meal, kept so Undo can put ALL of it back (#4971).
 *
 * The ingredient list is the obvious part. The other two are not, and leaving them out made Undo
 * quietly lossy: `updateMealAsync` drops `basedOnFoodId` and clears `autoPlanned` as soon as the
 * meal's set of foods stops matching the recipe it was logged from — which splitting a recipe
 * into its parts does immediately. Restoring only the ingredients therefore handed back the right
 * food but a meal that had forgotten it was ever that recipe, losing its recipe name and its
 * instructions for good.
 */
interface AutoTuneMealSnapshot {
  ingredients: Ingredient[];
  /** The recipe, or the earlier meal, this meal was logged from. Absent for an ordinary meal. */
  basedOnFoodId: string | undefined;
  /** Whether the meal was filled in by the planner rather than by the user. */
  autoPlanned: boolean | undefined;
}

/**
 * How the Auto-Tune confirmation LOOKS (#4971).
 *
 * At the bottom of the screen rather than the app-wide default of the top on a phone, for the
 * same reason the food-added confirmation is (see `client/src/util/food-added-toast.ts`): it
 * carries an Undo a user may want within a second of seeing it, and the top of a phone is the
 * furthest point from a thumb. On a wider screen `position` changes nothing, because every
 * toast is already at the bottom there.
 *
 * The colour is NOT set here — it says which of the two results happened, so the caller picks
 * it per toast.
 */
const AUTO_TUNE_TOAST_PRESENTATION = {
  position: 'bottom',
  duration: AUTO_TUNE_TOAST_DURATION_MS,
} as const satisfies Pick<ToastOptions, 'position' | 'duration'>;

/** One row of the "Add to this meal" block at the top of the meal screen (#4969). */
interface AddToMealRow {
  /** stable element id, so an end-to-end test can address one particular row */
  id: string;
  icon: string;
  title: TranslatedString;
  /**
   * The name this route goes by in the scrolling row, where a button is about 132px wide and
   * the name sits under the icon on one or two lines.
   *
   * Most routes use their full name here because it already fits. The ones that do not are
   * shortened rather than wrapped or cut ("Log Macros Manually" becomes "Macros"), and the
   * translations may shorten more aggressively than English needs to -- French renders that
   * same route as 35 characters. The grid always shows the full `title`, so nothing is lost:
   * the short name is only what the compact shape can hold.
   */
  shortTitle: TranslatedString;
  /** the grey line under the title, saying in a few words what this route is for */
  description: TranslatedString;
  /** if this is the row the eye should land on first, it is drawn more strongly */
  emphasized?: boolean;
  handler: () => void;
}

@Component({
  selector: 'page-edit-meal',
  templateUrl: 'edit-meal.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditMealPage extends CsPage<EditMealPageParams> {
  protected LL = LL;
  protected readonly NutrientDisplayMode = NutrientDisplayMode;
  protected readonly EditMealScope = EditMealScope;
  protected readonly NutritionTab = NutritionTab;

  protected title = '';

  /**
   * The meal's name for display, with a generated "Meal 3" translated (#4317).
   * Separate from `title`, which additionally appends the day name for meals
   * that are not today.
   */
  protected get mealDisplayName(): string {
    return formatScheduledMealName(this.meal?.name);
  }
  protected mealNames: MealNameSuggestion[] = [];
  protected meal: Log_Meal;
  protected day: Log_DayStart;
  protected dayName: string;

  /**
   * The day this meal belongs to, as an ISO date, for `ingredient-list` to pass to
   * the portion screen (see `EditIngredientPageParams.local`).
   *
   * Derived from the meal's own time through the shared helper, the same call this
   * page makes to find its day entry, so the two cannot answer differently.
   */
  protected get mealDayDate(): string | undefined {
    if (!this.meal) return undefined;
    return getEntryLocalTimeInDesiredTimeZone(this.meal, this.debug.getNowLocal()).toISODate();
  }
  protected timeLocal: string; // ISO string for meal.time_utc in local TZ (needed by Ionic DateTime picker)
  protected displayNutrientsMeal: UiDisplayNutrient[];
  protected displayNutrientsDay: UiDisplayNutrient[];

  protected selectedScope: EditMealScope = EditMealScope.Meal;
  protected selectedDisplayMode: NutritionTab = NutritionTab.Logged;

  protected totaForDayExpanded = false;
  protected mealExpanded = false;
  protected recipeInfoExpanded = false;

  protected tuningSource: ComplexFood | undefined = undefined;
  /** The marker `tuningSource` was last loaded for, so a marker changed underneath the page is noticed. */
  private tuningSourceLoadedFor: string | undefined = undefined;
  /** Counts runs of `cacheTuningValuesAsync`, so only the newest one publishes its source. */
  private tuningSourceRun = 0;
  protected recipeFirstLine = '';

  protected get cardTitle(): TranslatedString {
    if (this.selectedScope === EditMealScope.Meal) {
      return this.selectedDisplayMode === NutritionTab.Logged
        ? LL.editMeal.macrosLogged()
        : LL.editMeal.macrosRemaining();
    } else {
      return this.selectedDisplayMode === NutritionTab.Logged
        ? LL.editMeal.dayMacrosLogged()
        : LL.editMeal.dayMacrosRemaining();
    }
  }

  protected get activeDisplayNutrients(): UiDisplayNutrient[] {
    return this.selectedScope === EditMealScope.Meal
      ? this.displayNutrientsMeal
      : this.displayNutrientsDay;
  }

  protected get activeNutrientDisplayMode(): NutrientDisplayMode {
    return this.selectedDisplayMode === NutritionTab.Logged
      ? NutrientDisplayMode.Logged
      : NutrientDisplayMode.Remaining;
  }

  /**
   * Whether the small macros card at the top of the screen is worth drawing.
   *
   * It summarises what is in the meal, so an empty meal has nothing to summarise -- there the
   * routes into the meal are the whole point of the screen and the card would only push them
   * down. `ingredients` is defaulted because a meal written by an older version of the app can
   * arrive without the field at all.
   */
  protected get showCompactMacros(): boolean {
    return (this.meal?.ingredients?.length ?? 0) > 0;
  }

  /**
   * What the small card's bar row shows.
   *
   * Each bar measures one macro against its own target, which needs there to BE targets. A
   * meal the user made up themselves has none, so there the single bar shows how the meal's
   * own calories divide between its macros instead. The day always has targets.
   */
  protected get compactMacrosBarStyle(): NutrientBarStyle {
    return this.selectedScope === EditMealScope.Day || this.isMealScheduled()
      ? NutrientBarStyle.Targets
      : NutrientBarStyle.Distribution;
  }

  private initializing = true;
  private docWatcher: DocWatcher;


  AuthRole = AuthRole;

  @ViewChild('ingredientsCard') ingredientsCard: IngredientList;

  constructor(
    public navCtrl: NavController,
    private alertCtrl: AlertController,
    public userDb: UserDb,
    public foodDb: FoodDbService,
    public auth: AuthService,
    private pubSub: PubSubService,
    private settings: SettingsService,
    private debug: DebugService,
    private cd: ChangeDetectorRef,
    private telemetry: TelemetryService,
    public logging: LoggingService,
    private toastCtrl: ToastController,
    private mealActionsService: MealActionsService,
    private configDb: ConfigDbService,
  ) {
    super();
  }

  /** any scanner model configured at all -- what the nutrition-label scanner needs (#3783) */
  protected scanLabelEnabled = false;

  /**
   * Whether the "Add to this meal" routes are drawn as the full-width grid or as the one
   * scrolling row.
   *
   * The add-routes are the whole answer to "what do I do here?" on a meal with nothing in it,
   * but on a meal that already holds food a grid of them is a wall between the user and the
   * thing they opened the meal to look at. So the meal's own contents choose, and this is
   * WORKED OUT each time the template asks rather than stored in a flag.
   *
   * That is deliberate, and it is the second attempt. A stored flag has to be updated by every
   * path that can change what the meal holds, and there are more of those than they look: the
   * ingredient list raises one event when it hands over a new list of ingredients and a
   * different one when it has merely reloaded the same ones, and coming back from the Foods
   * page with a food takes the second. Miss one path and the routes are left in the wrong
   * layout with nothing reporting it -- which is exactly what happened: a meal filled from the
   * Foods page kept the grid until the user left the screen and came back.
   *
   * Nothing is persisted, and there is nothing to keep in step.
   */
  protected get routesAsGrid(): boolean {
    return this.currentIngredientCount() === 0;
  }

  /**
   * How many things the meal holds right now.
   *
   * **The meal is asked first, and the order matters.** The obvious alternative -- ask the
   * ingredient list, since it is the component that changes this -- reads a value that Angular
   * updates LATER in the same pass than it reads this one: the two bindings that consume this
   * (the `open` class and `inert`) sit at the top of the template, while the list's own
   * `[ingredients]` input is set further down. So on any path where the PAGE builds a new array
   * -- copying a day, a meal photo scan, filling from a recipe, undoing an auto-tune -- asking
   * the list would read its OLD array, and the value would change again before the pass ended.
   * In a development build that is an ExpressionChangedAfterItHasBeenChecked error; in a
   * production one it is one frame drawn with the wrong layout.
   *
   * Nothing is lost by asking the meal instead. Every path where the LIST changes the meal's
   * contents ends by handing the page its new array synchronously (`updateIngredients.emit` at
   * the end of `handleEditResult`), so the meal is already current by the time anything renders.
   *
   * The list is kept as a fallback for the case the meal cannot answer at all. Both sides are
   * treated as possibly missing -- a meal document written by an older version of the app may
   * have no ingredients array.
   */
  private currentIngredientCount(): number {
    if (Array.isArray(this.meal?.ingredients)) return this.meal.ingredients.length;
    const listed = this.ingredientsCard?.ingredients;
    return Array.isArray(listed) ? listed.length : 0;
  }

  /**
   * How often this user takes each route into a meal. Read from the synced settings when the
   * page loads a meal, and rewritten on every route the user takes. See `add-route-usage.ts`.
   */
  private routeUsageScores: RouteUsageScores = {};

  /**
   * The copy of those scores the CURRENT screen is ordered by.
   *
   * It is taken once, when the page loads a meal, and never changes while the user is
   * looking at it. Ordering straight from the live scores instead would move a button under
   * the finger that just tapped it: taking a route immediately raises its score, so the
   * strip would rearrange itself on the way to the page it just opened.
   */
  private routeOrderSnapshot: RouteUsageScores = {};

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      // this.meal is the canonical object (identity map), so sync already updated it in-place.
      // Recalculate nutrient totals from the updated reference.
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        // if the meal is not loaded yet (or its load failed the page guard), there is nothing to
        // recalculate: the first-load path below draws the totals itself once the meal is in.
        // The read that loads the meal is handed this watcher, so sync can call back first.
        if (!this.meal) return;
        // skip re-render while drag-reorder is active — DOM invalidation breaks the drag
        if (this.ingredientsCard?.dragService.isAnyDragActive()) return;
        this.updateTotalNutrientsHandler(this.meal.nutrients);
        // the marker changed underneath this page -- the portion screen's Undo rewrites this
        // same object after a split from Home (#6188), and sync can too. Reload the source, or
        // the "Based on" line and Auto-Tune keep using the one loaded for the old marker.
        if (this.meal.basedOnFoodId !== this.tuningSourceLoadedFor) {
          void this.cacheTuningValuesAsync().then(() => this.cd.markForCheck());
        }
        this.cd.markForCheck();
      }, { label: 'edit-meal' });
    }

    // if returning from a sub-page, skip re-initialization
    // returnCallbacks handle any changes (e.g. onIngredientEdited)
    if (this.meal) {
      let dayDate = getEntryLocalTimeInDesiredTimeZone(this.meal, this.debug.getNowLocal());
      ({ day: this.day } = await this.userDb.getDayMealsAsync(dayDate));
      this.updateTotalNutrientsHandler(this.meal.nutrients);
      return;
    }

    let now = this.debug.getNowLocal();
    let dayDate: DateTime = undefined;

    let id = this.navParams.data.id;
    if (id) {
      try {
        this.meal = await this.userDb.getOneLogEntryAsync<Log_Meal>(id, this.docWatcher);
        dayDate = getEntryLocalTimeInDesiredTimeZone(this.meal, this.debug.getNowLocal());
      } catch (err) {
        return this.auth.failPageGuardAsync(`Unknown meal id: '${id}'`, { cause: err });
      }
    } else {
      dayDate = DateTime.fromISO(this.navParams.data.day);
      if (!dayDate.isValid) {
        dayDate = now;
      }
      let suggestion = await this.foodDb.suggestMealNameAsync(dayDate, this.docWatcher);

      let hour = Math.trunc(suggestion.time_hr);
      let minute = Math.round((suggestion.time_hr - hour) * 60);
      let time = dayDate.set({ hour, minute });

      // pass the LOCAL time, not `time.toUTC()`. createMealEntry takes both of the meal's
      // time fields from this one argument — the instant and the writing device's offset from
      // UTC — and they only mean anything as a pair (see design-docs/sync-integrity.md, "A Log
      // Entry's Two Time Fields Must Always Move Together"). Converting first kept the instant
      // right and made the offset zero, so every later read subtracted the user's own offset:
      // a meal added at 14:00 in Madrid read 12:00, and near either end of the day it moved to
      // the day before or after, taking its calories with it.
      this.meal = this.userDb.createMealEntry(time, suggestion.name);
    }

    this.dayName = formatRelativeDay(now, dayDate);

    ({ day: this.day } = await this.userDb.getDayMealsAsync(dayDate));

    // if the day holds no day-start entry, this screen carries on without day totals
    if (!this.day) {
      // The PROBLEM tier, not info: the screen repairs what it SHOWS -- the Day side of the
      // scope dial draws zeros instead of crashing the page (see updateTotalNutrientsHandler)
      // -- but nothing has repaired the data underneath. A day that holds a meal and no day
      // record is unexplained, and the one production report we have is too thin to reason
      // from, so this is how we learn whether a real user reaches it and by what route.
      // Reported here, on the path that loads a meal, so it is once per page load: the
      // early-return branch above and the docWatcher callback both run again and again on
      // one visit.
      this.logging.trackSilentProblem('MealDayHasNoDayStart', {
        mealId: this.meal?._id,
        day: dayDate.toISODate(),
      });
    }

    this.meal.ingredients.forEach(ingredient => {
      this.foodDb.setIngredientStatus(ingredient);
    });

    await this.cacheTuningValuesAsync();

    if (!this.meal.nutrients) {
      this.meal.nutrients = {}; // handles stale/damaged objects
    }

    // a generated name like "Meal 3" is stored in English, so translate it for
    // display; a name the user typed passes through unchanged (#4317)
    this.title = formatScheduledMealName(this.meal.name);

    // append date to meals that are not today
    let mealDate = DateTime.fromISO(this.meal.time_utc).toLocal();
    if (!mealDate.hasSame(now, 'day')) {
      this.title += ', ' + this.dayName;
    }

    this.timeLocal = formatTime(DateTime.fromISO(this.meal.time_utc).toLocal(), this.settings)

    // The routes are ordered by this user's own usage, and that order is decided ONCE here
    // rather than re-derived as the page runs -- see routeOrderSnapshot for why re-sorting
    // under the user is the thing to avoid.
    this.routeUsageScores = readRouteUsage(
      this.settings.mealAddRouteUsageJSON,
      // the PROBLEM tier, not info: the screen repaired what it SHOWS by falling back to the
      // measured order, but the damaged value is still in the settings document and whatever
      // damaged it is still doing so. Nothing except this screen writes that field, so a
      // shape we cannot read is somebody's bug, and it needs a person eventually -- just not
      // an interruption now, since the user sees a working screen.
      err => this.logging.trackSilentProblem('MealAddRouteUsageUnreadable', {
        error: describeErrorForLog(err),
      }),
    );
    this.routeOrderSnapshot = { ...this.routeUsageScores };

    // during initialization, recalculate nutrients but don't save (prevents unnecessary DayUpdatedEvent
    // which causes home page content to flicker)
    this.initializing = true;
    await this.updateMealAsync();
    this.initializing = false;
    this.updateTotalNutrientsHandler(this.meal.nutrients);
    void this.loadDataAsync(); // we deliberately allow this to run in background because it is not important immediately
  }

  // Note: Callbacks are now handled via the returnCallback mechanism in NavController.
  // Parent pages should use returnCallback param instead of passing callback functions.
  // Loading spinners should be added to the parent's callback method if needed.

  protected getTuningTitle() {
    if (!this.tuningSource) {
      return undefined;
    }
    if (isLogMeal(this.tuningSource)) {
      return LL.editMeal.basedOn({ 
        name: this.tuningSource.name, 
        day: formatRelativeDay(this.debug.getNowLocal(), DateTime.fromISO(this.tuningSource.time_utc)) 
      });
    } else {
      return LL.editMeal.recipeInstructions({ name: this.tuningSource.name });
    }
  }

  private async cacheTuningValuesAsync() {
    // several of these can be running at once, each started by a marker change. Only the newest
    // may publish: an older run finishing last would install the source of a marker the meal no
    // longer has, and the next save would then delete the right marker against it (#6188).
    const run = ++this.tuningSourceRun;
    this.tuningSource = undefined;
    this.tuningSourceLoadedFor = this.meal.basedOnFoodId;
    let source: ComplexFood | undefined;
    if (this.meal.basedOnFoodId !== undefined) {
      let food: AnyFood;
      if (isLogMealById(this.meal.basedOnFoodId)) {
        food = await this.userDb.getOneLogEntryAsync<Log_Meal>(this.meal.basedOnFoodId, this.docWatcher);
      } else {
        food = await this.foodDb.getFoodByIdAsync(this.meal.basedOnFoodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      }
      if (run !== this.tuningSourceRun) return;

      if (isComplexFood(food)) {
        source = food as ComplexFood;
      }
    }

    if (!source && this.meal.ingredients && this.meal.ingredients.length === 1) {
      let ingredient = this.meal.ingredients[0];
      if (isIngredientReal(ingredient)) {
        let onlyFood = await this.foodDb.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
        if (run !== this.tuningSourceRun) return;
        if (isComplexFood(onlyFood)) {
          source = onlyFood;
        }
      }
    }
    this.tuningSource = source;

    let recipeInstructionsMD = this.tuningSource && (this.tuningSource as Recipe).instructions || '';
    const recipeInstructionsHtml = marked(recipeInstructionsMD, { async: false });

    // Remove HTML tags to get plain text
    const plainText = recipeInstructionsHtml.replace(/<[^>]+>/g, '');
    this.recipeFirstLine = plainText.slice(0, 1000);
  }

  async ionViewDidEnter() {
    // Navigation history is now handled automatically by trail-based navigation
  }

  async loadDataAsync() {
    this.mealNames = await this.foodDb.getMealNameSuggestionsAsync(this.docWatcher);
    this.scanLabelEnabled = !!(await this.configDb.getConfigValueAsync<string>(ConfigKey.ScanProvider));
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  /**
   * Rebuilds both halves of the macros card: the meal's own numbers, and the whole day's.
   *
   * `this.day` is absent when the meal's day holds no day-start entry. `getDayMealsAsync`
   * answers that way on purpose rather than treating it as a fault -- see the comment on
   * `getMealTargetsAsIfEmptyAsync` in user-db.service.ts -- and the sibling screens that read a
   * day already cope with it. This one used to read straight through it, which threw inside
   * `ionViewCanEnter` and left the user with "This couldn't be opened" on every single meal
   * they tapped. An empty nutrients object is the honest answer here.
   *
   * What the user then sees on the Day side is `0` for every macro with no target beside it,
   * which is the compact card's ordinary "nothing to show" rendering -- `getCompactMacroValue`
   * in nutrient-list.ts rounds an absent value to zero. So it reads the same as a day that has
   * a record and nothing logged against it, which is a real limitation rather than a nicety:
   * teaching that card a "not available" rendering means changing a component the portion
   * screen shares, and its layout was settled by the product owner on 2026-09-11 (#5327).
   * `FormatNutrients` does mark an absent value "--", but only in its `valueShort` field, which
   * this card does not read.
   *
   * The Day button itself deliberately stays put, for the same #5327 reason: taking a button
   * away would reopen the decision that the two dials share one row.
   */
  updateTotalNutrientsHandler(nutrients: Nutrients) {
    // Using OnPush change detection
    this.displayNutrientsMeal = FormatNutrients(nutrients, this.userDb, { targets: this.meal.targets });
    this.displayNutrientsDay = FormatNutrients(this.day?.nutrients || {}, this.userDb, { targets: this.day?.targets });

    this.cd.markForCheck();
  }

  async updateIngredientsHandler(ingredients: Ingredient[]) {
    this.meal.ingredients = ingredients;
    await this.updateMealAsync();
    this.cd.markForCheck();
  }

  /**
   * Runs one route, and counts that the user took it.
   *
   * Every tap goes through here, from the row and from the grid alike, so the count measures
   * the route rather than the place it was tapped. The score is saved
   * immediately -- the route usually navigates away, so there is no later moment to save in.
   *
   * The order on screen deliberately does NOT change now; it is fixed for this visit (see
   * routeOrderSnapshot).
   */
  protected onRouteTapped(row: AddToMealRow) {
    this.routeUsageScores = recordRouteUse(this.routeUsageScores, row.id);
    this.settings.mealAddRouteUsageJSON = JSON.stringify(this.routeUsageScores);
    this.settings.saveInBackground('meal add-route usage');

    row.handler();
  }

  /** The ingredient list set or cleared the meal's source marker. `undefined` clears it (an Undo). */
  updateBasedOnFoodIdHandler(newBasedOnFoodId: string | undefined) {
    this.setBasedOnFoodId(newBasedOnFoodId);
    void this.cacheTuningValuesAsync();
    // Using OnPush change detection
    this.cd.markForCheck();
  }

  /**
   * Set or clear the meal's source marker, and forget the source loaded for the OLD marker.
   *
   * The forgetting is not tidiness (#6188). `updateMealAsync` deletes the marker when the meal's
   * foods no longer match the loaded source, and the next save usually follows at once. Left in
   * place, the old source would be compared against the new foods, and a marker that was just
   * set -- or just put back by Undo -- would be deleted again. With no source loaded the check is
   * skipped, and `cacheTuningValuesAsync` loads the right one.
   */
  private setBasedOnFoodId(marker: string | undefined) {
    if (marker === undefined) {
      delete this.meal.basedOnFoodId;
    } else {
      this.meal.basedOnFoodId = marker;
    }
    this.tuningSource = undefined;
  }

  ingredientAddedHandle() { }

  /**
   * Recalculate the meal's nutrients and save it.
   *
   * Answers whether the meal actually reached the database. A food that cannot be read stops the
   * recalculation, and this method reports that to the user itself and returns without saving —
   * so a caller that goes on to tell the user something about the saved meal has to know the save
   * did not happen, or it states the opposite of the alert the user is already looking at
   * (#5297). Most callers have nothing to say afterwards and can ignore the answer.
   */
  async updateMealAsync(): Promise<boolean> {
    // if the user changes any ingredients in a meal (instead of just changing the amount), make sure the original recipe reference is cleared.
    // "changes the ingredients" means the SET of foods differs from the source; the old check only cleared on zero overlap, so keeping one shared food kept a stale recipe name on the card (#4112).
    if (
      this.tuningSource &&
      !ingredientFoodSetsMatch(this.meal.ingredients, this.tuningSource.ingredients)
    ) {
      delete this.meal.basedOnFoodId;
      // if user manually changes ingredients after auto-planning, clear the auto-planned flag
      if (this.meal.autoPlanned) {
        this.meal.autoPlanned = false;
      }
    }

    // recalculate meal's nutrients. A food that could not be READ makes this throw rather than
    // quietly producing a total with that ingredient missing from it, so nothing below runs and
    // the meal is not saved — the STORED total keeps its previous, correct value.
    //
    // Unlike the other callers, this one does NOT undo the edit that got us here. Whoever called
    // updateMealAsync has already changed this.meal (a new ingredient list, a suggestion, another
    // day's meal copied in), and this is the page the user is editing on: putting their change
    // back would silently undo what they just did, in front of them. So the edit stays on screen,
    // unsaved, next to a total that still describes the old ingredients, and the next successful
    // recalculation writes both together. The alert tells them why nothing was saved.
    //
    // During initialization nothing was going to be saved anyway, and the ingredient list below
    // shows its own placeholder for the same condition, so stay silent there rather than opening
    // an alert over a page that is still loading. Signing out is silent for the same reason it is
    // everywhere else here — see design-docs/warm-resume.md's sign-out race convention.
    try {
      await this.foodDb.recalculateMealNutrientsAsync(this.meal);
    } catch (e) {
      if (e instanceof FoodReadFailedError) {
        if (!this.initializing && !this.viewDestroyed && !this.auth.isLikelySignOutRace()) {
          await showFoodLoadFailedAlertAsync(this.alertCtrl);
        }
        return false;
      }
      throw e;
    }

    await this.cacheTuningValuesAsync();

    // update nutrient state on updates, based on current state and nutrient target match
    // don't make any changes for Skipped and Logged meals
    const { state } = this.meal;
    if (state !== LogRecordState.Skipped && state !== LogRecordState.Done) {
      const anyFoodSelected = this.meal.ingredients.length > 0;

      if (anyFoodSelected && state === LogRecordState.Scheduled) {
        this.meal.state = LogRecordState.Planned;
      }

      if (!anyFoodSelected && state === LogRecordState.Planned) {
        this.meal.state = LogRecordState.Scheduled;
      }
    }

    // save to DB (skipped during initialization to prevent unnecessary DayUpdatedEvent)
    if (!this.initializing) {
      await this.saveMealAsync(this.meal);
    }
    return true;
  }

  async saveMealAsync(meal: Log_Meal) {
    let time = getEntryLocalTimeInDesiredTimeZone(meal, this.debug.getNowLocal());
    // Take the day from the SAME read as the meals, and store it, rather than trusting the copy
    // taken when the page opened. A day with no day-start entry does not stay that way: the
    // schedule rebuild writes one for every scheduled day, so a page opened without one can be
    // saved with one now in place. Asking the stale copy would skip the day totals below and
    // leave the day short by this meal, quietly -- before this method had a guard at all, the
    // same staleness threw instead. Storing it also keeps the macros card, redrawn at the foot
    // of this method, showing the day this save actually wrote to. With a day record present
    // this changes nothing: `getDayMealsAsync` reads through the identity map, so it hands back
    // the very object `this.day` already points at.
    let { day, meals } = await this.userDb.getDayMealsAsync(time);
    this.day = day;

    let existingMealIndex = meals.findIndex(e => e._id === meal._id);
    if (existingMealIndex > -1) {
      meals.splice(existingMealIndex, 1, meal);
    }

    let modifiedMeals: boolean[] = [];
    let entriesToSave = [];

    // if the day has a day-start entry, refresh its totals and save it alongside the meal.
    //
    // With no such entry there is nothing to do here and nothing to save: the day has no
    // document to hold totals. `updateDayValuesInMemory` reads the day's own time on its first
    // line and would throw on a missing one, and pushing `undefined` into the save set would
    // throw inside the save. `updateDayValuesOnDiskAsync` in the same service already declines
    // on this same condition. Skipping the block also skips the planning-target pass that loop
    // runs over the day's OTHER meals, which is right for the same reason: those targets are
    // derived from the day's totals and the day's targets, and neither exists. The meal the
    // user is editing is still saved, by the `entriesToSave.find` check below.
    if (this.day) {
      this.userDb.updateDayValuesInMemory(this.day, meals, modifiedMeals);

      let i = -1; // starting from -1 to match the loop in updateDayValuesInMemory()
      for (let m of meals) {
        i++;
        if (modifiedMeals[i]) {
          entriesToSave.push(m);
        }
      }
      entriesToSave.push(this.day);
    }

    this.updateTotalNutrientsHandler(meal.nutrients);
    if (!entriesToSave.find(e => e._id === meal._id)) {
      entriesToSave.push(meal);
    }
    await this.userDb.saveEntriesAsync(DatabaseWrapper.getTransactionId(), entriesToSave);

    // make sure this page is synced to new data (the function that called this might have changed something, and the updateDayValuesAsync() above also changes things)
    this.cd.markForCheck();
    // this is needed to update view on home page if returning from this page
    this.pubSub.publish(DayUpdatedEvent, {});
  }

  async saveAsRecipeAsync() {
    await this.alertCtrl.showAlertAsync({
      title: LL.foods.buttons.createRecipe(),
      inputs: [
        {
          name: 'name',
          placeholder: LL.editMeal.placeholders.recipeName(),
          // seed with the DISPLAY name (#4317). Whatever the user accepts becomes a
          // recipe name, which is free user text they can see and edit here — so a
          // translated seed is correct, and leaving the raw English "Meal 3" would be
          // the only untranslated word in an otherwise fully translated dialog.
          value: this.mealDisplayName,
          label: LL.editMeal.labels.recipeName(),
        },
      ],
      buttons: [
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
        },
        {
          text: LL.common.buttons.save(),
          role: AlertButtonRole.Primary,
          result: async data => {
            let newRecipe = this.foodDb.createNewRecipe();
            newRecipe.ingredients = this.meal.ingredients;
            newRecipe.name = data.name;
            newRecipe.nutrients = this.meal.nutrients;
            newRecipe.instructions = '';
            void this.foodDb.saveUserFoodAsync(newRecipe, 'used');
            this.telemetry.trackEvent('action_recipe_saveas', {});
          },
        },
      ],
    });
  }

  async copyFromDay(_e: Event) {
    const todayMealDate = DateTime.fromISO(this.meal.time_utc).toLocal();
    const result = await this.navCtrl.callAsync(IInputCalendarPage, {
      dateISO: todayMealDate.toISODate(),
      maxDateISO: todayMealDate.minus({ days: 1 }).toISODate(),
    });
    if (!result?.dateISO) {
      return;
    }
    const date = DateTime.fromISO(result.dateISO);
    const dateBOD = date.startOf('day');
    const dateEOD = date.endOf('day');
    const meals = (await this.userDb.getLogEntriesInRangeAsync(dateBOD, dateEOD, this.docWatcher)).filter(e => e.type === LogRecordType.Meal);
    const meal = meals.find(e => e.name === this.meal.name) as Log_Meal | undefined;
    // if the chosen day holds no meal with this name, say so and change nothing. The calendar
    // above offers every earlier day, including days before the user started logging and days
    // whose meals are named differently, so finding nothing is an ordinary outcome rather than
    // a fault. Without this the next line dereferenced undefined, and because the popup-menu
    // handler discards the promise the throw surfaced as an unhandled rejection: the menu just
    // closed and nothing told the user why.
    if (!meal) {
      await this.alertCtrl.showAlertAsync({
        title: LL.editMeal.alerts.copyFromNoMatchTitle(),
        // the day itself is deliberately not named. formatRelativeDay renders "Yesterday" as
        // often as a date, and "logged on Yesterday" reads badly in English and worse in the
        // three translated languages. The user picked the day a moment ago, so saying which
        // meal is missing is enough.
        message: LL.editMeal.alerts.copyFromNoMatchMessage({
          mealName: formatScheduledMealName(this.meal.name),
        }),
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return;
    }
    // add the earlier day's foods to this meal rather than replacing what is already in it,
    // and clone them on the way in. mergeCopiedMealIngredients' own doc comment says why on
    // both counts.
    //
    // the earlier day's meal came straight out of the database a few lines above. Every meal
    // this app WRITES has the array -- createMealEntry always sets it -- but a meal written by
    // an older version may not. The read path now defaults it: backCompatMealEntry gives a
    // stored meal an empty list when it has none. This default stays anyway, because a guard
    // against an old-shaped document is never removed on the strength of what writes the
    // document today -- which is also why the other readers of this same field all keep
    // theirs (edit-ingredient.ts:1042 and :1356 do it for this very "copy an earlier meal"
    // operation).
    //
    // Defaulting is the whole answer here, and hides nothing: a missing array and an empty one
    // mean the same thing to this operation -- there is nothing to copy -- and both leave this
    // meal untouched. Without it the spread inside the helper throws, and because the
    // popup-menu handler discards this promise the user just sees the menu close with nothing
    // happening: the same failure the comment above describes for a missing meal.
    //
    // an empty meal takes the copied meal's source marker with its foods, as every other way of
    // reusing a past meal does (#6188). Set before the save, and with the old source dropped, so
    // the save does not compare the new foods against a source they never came from.
    const mealWasEmpty = (this.meal.ingredients ?? []).length === 0;
    this.meal.ingredients = mergeCopiedMealIngredients(this.meal.ingredients, meal.ingredients ?? []);
    const marker = markerAfterAdding(mealWasEmpty, this.meal.basedOnFoodId, meal.basedOnFoodId);
    if (marker !== this.meal.basedOnFoodId) {
      this.setBasedOnFoodId(marker);
    }
    await this.updateMealAsync();
    if (this.viewDestroyed) return;
    // the list renders from its own loaded rows, so it has to be rebuilt for the added foods
    // to appear — the same step onMealSuggestionResult takes after it fills a meal.
    await this.ingredientsCard.loadIngredientsAsync();
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  /**
   * A skipped meal is food nobody ate, so the day's totals leave it out. This screen can still
   * open one and add food to it, and updateMealAsync deliberately leaves the skip in place, so
   * without a notice the food is stored and the day's totals silently do not move
   * (df-a5fbe964df94). The template shows a notice with a Restore action while this is true.
   */
  get isMealSkipped(): boolean {
    return this.meal?.state === LogRecordState.Skipped;
  }

  /** Set by restoreSkippedMealAsync the moment it accepts a tap, before it awaits anything. */
  private restoreSkippedMealTapped = false;

  /**
   * Says that a Restore tap on the skipped notice was accepted AND the meal is no longer
   * skipped -- so the restore really happened on this screen, rather than the meal simply
   * never having been skipped.
   *
   * The template writes it out as an attribute for the E2E test that taps Restore
   * (`client/src/e2e/repro/skipped-meal-notice-restore.spec.ts`). That test's only end state
   * used to be the notice leaving the page, and a tap the browser silently loses -- which the
   * nightly does now and then -- looks exactly like a tap that landed and did nothing. Waiting
   * for this instead lets the click helper tap again until the handler has demonstrably run.
   *
   * The flag on its own would say only that a tap arrived. Pairing it with `!isMealSkipped` is
   * what makes this report the restore having TAKEN, and it correctly goes away again if the
   * meal is skipped once more on this same screen.
   *
   * Nothing has to reset the flag between visits. A page that leaves the navigation stack has
   * its component destroyed (`cs-nav`'s destroyRemovedPage), so the next open starts false; and
   * a page kept while a sub-page sits over it is still showing the same meal, because
   * `ionViewCanEnter` returns early for as long as `this.meal` is set.
   */
  get skippedMealRestored(): boolean {
    return this.restoreSkippedMealTapped && !this.isMealSkipped;
  }

  /**
   * Un-skip the meal from this screen. Takes the same state the day list's Restore Task action
   * sets (cs-day-items' handleRevertButton): Planned when the meal holds food, Scheduled when it
   * is empty. saveMealAsync then recomputes the day's totals, which now include this meal.
   *
   * Tapping twice is harmless: the guard on the first line returns once the meal is restored.
   */
  async restoreSkippedMealAsync() {
    if (!this.isMealSkipped) return;
    this.restoreSkippedMealTapped = true;
    this.meal.state = this.meal.ingredients.length > 0 ? LogRecordState.Planned : LogRecordState.Scheduled;
    // zoneless: draw the notice's removal now, rather than leaving the user looking at a notice
    // that says the meal is skipped until the save below has been round-tripped to the database.
    this.cd.markForCheck();
    await this.saveMealAsync(this.meal);
  }

  isMealScheduled() {
    return this.meal && this.meal.targets && Object.keys(this.meal.targets).length > 0;
  }

  async deleteMealAsync() {
    this.telemetry.trackEvent('action_meal_delete', {});

    // for scheduled meals, use centralized clear meal service
    if (this.isMealScheduled()) {
      const cleared = await this.mealActionsService.clearMealAsync(this.meal);
      if (cleared) {
        // refresh UI after clearing
        // meal was already saved by clearMealAsync
        await this.ingredientsCard.loadIngredientsAsync();
        await this.cacheTuningValuesAsync();
        this.updateTotalNutrientsHandler(this.meal.nutrients);
        this.cd.markForCheck();
      }
      return;
    }

    // for unscheduled meals, show delete confirmation and remove entry
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.editMeal.alerts.deleteMealTitle({ mealName: formatScheduledMealName(this.meal.name) }),
      message: LL.editMeal.alerts.deleteMealMessage(),
      enableBackdropDismiss: true,
      buttons: [
        {
          text: LL.common.buttons.delete(),
          role: AlertButtonRole.Primary,
          result: true,
        },
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
        },
      ],
    });

    if (confirmed) {
      await this.userDb.deleteLogEntryAsync(this.meal, DatabaseWrapper.getTransactionId());
      void this.navCtrl.popAsync();
    }
  }

  async handleAutoPlan() {
    this.telemetry.trackEvent('meal_auto_plan', {});

    // Working out what to suggest reads the day from the database, so it can fail. Before this
    // read existed the button could not fail at all, and its callers still discard the promise —
    // so the read must report its own failure rather than leave the button doing nothing.
    const tuningTargets = await this.userDb.getMealTargetsAsIfEmptyOrReportAsync(
      this.meal, LL.editMeal.errors.couldNotSizeSuggestions());
    if (!tuningTargets) return;
    if (this.viewDestroyed) return;

    const hasIngredients = this.meal.ingredients.length > 0;
    let params: MealSuggestionsPageParams = {
      title: this.title,
      mode: hasIngredients ? MealSuggestionsMode.Replace : MealSuggestionsMode.Add,
      returnCallback: 'onMealSuggestionResult',
      local: {
        targets: this.meal.targets,
        // What a suggested meal should contain — see `mealTargetsAsIfEmpty` (#5297).
        tuningTargets,
      },
    };
    void this.navCtrl.pushAsync(IMealSuggestionsPage, params);
  }

  /**
   * Callback invoked by NavController when returning from MealSuggestionsPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  async onMealSuggestionResult(_context: unknown, suggestion: MealTunerMeal | undefined) {
    if (suggestion) {
      // Using OnPush change detection
      await this.foodDb.fillMealFromTunerAsync(this.meal, suggestion);
      this.meal.autoPlanned = true;
      await this.updateMealAsync();
      await this.foodDb.markMealAsUsedAsync(this.meal);
      await this.ingredientsCard.loadIngredientsAsync();
      this.cd.markForCheck();
    }
    // if user cancelled, no action needed
  }

  handleMealPhotoScan() {
    this.telemetry.trackEvent('meal_photo_scan_open', {});
    void this.navCtrl.pushAsync(IMealPhotoScannerPage, {
      mode: 'add',
      returnCallback: 'onMealPhotoScanResult',
      // The scanner does not read any of these. It forwards them to the portion screen it
      // opens when a row is tapped, which is what lets that screen offer its `Day` scope --
      // the same three values `ingredient-list` passes when a food is added here directly.
      // The ingredients go with them because this scan ADDS to the meal, so what the meal
      // already holds is still part of the day after the save.
      local: mealPhotoScanMealParams(this.meal, this.meal.ingredients, this.debug.getNowLocal()),
    });
  }

  async onMealPhotoScanResult(_context: unknown, result: MealPhotoScannerResult) {
    if (!result?.matchedFoods?.length) return;

    // build new ingredients array (new reference so OnPush detects the change)
    const newIngredients: Ingredient[] = [...this.meal.ingredients];

    for (const matched of result.matchedFoods) {
      const food = await this.foodDb.getFoodByIdAsync(matched.food._id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (!food) continue;

      const ingredient: IngredientReal = {
        type: IngredientType.Real,
        foodId: food._id,
        name: food.name,
        weight_g: matched.estimatedGrams,
        // keep the "Weighed with peel" tick from the scanner's portion screen (#5544)
        ...(matched.weighedWithRefuse ? { weighedWithRefuse: matched.weighedWithRefuse } : {}),
      };
      this.foodDb.setIngredientStatus(ingredient);
      newIngredients.push(ingredient);
      void this.foodDb.markFoodAsUsedAsync(food);
    }

    this.meal.ingredients = newIngredients;
    await this.updateMealAsync();
    if (this.viewDestroyed) return;
    await this.ingredientsCard.loadIngredientsAsync();
    this.cd.markForCheck();
  }

  // redirect from the meal photo scanner's packaged-product alert (#3902): the
  // scanner replaced itself with the Foods page so the user could scan a
  // barcode / nutrition label, and they picked a food. Add it to this meal by
  // reusing the normal "add food" path — it APPENDS (never wipes the meal) and
  // handles every Foods-page action (row tap, custom macros). The per-card "+"
  // quick-add was a third action until #4967 removed that button.
  onMealScanRedirectFood(_context: unknown, result: FoodsPageResult) {
    // Deliberately not awaited. This arrives from the scanner's own redirect, which has already
    // left the navigation stack — there is no caller here waiting to hear whether the add
    // finished, unlike the food list's pick (#4967), which is why that path awaits and this
    // one does not.
    void this.handleFoodPageResult({ source: 'addFood' }, result);
  }

  shouldShowAutoTuneButton() {
    if (this.auth.getAuthRole() < AuthRole.Paid || this.meal.planningTargets == null) {
      return false;
    }

    return this.meal.ingredients && this.meal.ingredients.length > 0;
  }

  async handleAutoTune() {
    this.telemetry.trackEvent('action_meal_autotune', {});

    // The one yardstick for this whole operation — see `mealTargetsAsIfEmpty` (#5297). The tuner
    // aims at it AND the verdict is measured against it; those used to be different figures.
    //
    // Read FIRST, before anything at all is changed. Working it out needs the day from the
    // database, so it can fail — and nothing awaits this method, so an uncaught failure would
    // reach the user as a button that silently does nothing. Reading it here rather than at
    // tuning time means a failure happens while the meal is still untouched: there is no saved
    // recipe split to explain or put back, only a message. The answer cannot go stale in the
    // meantime, because the calculation leaves this meal out of the day total entirely, so
    // splitting or tuning this meal cannot move it.
    const asIfEmptyTargets = await this.userDb.getMealTargetsAsIfEmptyOrReportAsync(
      this.meal, LL.editMeal.errors.autoTuneFailed());
    if (!asIfEmptyTargets) return;
    if (this.viewDestroyed) return;

    // if the rest of the day already used up its calories, the figure carries no calorie target and
    // the tuner refuses it as a crash -- so tune against the meal's own plan figure instead. an
    // over-eaten day is not a fault to report. `tunableTargetsOr` is the one place that rule lives,
    // and it mirrors the tuner's own refusal rule.
    const tuningTargets = tunableTargetsOr(asIfEmptyTargets, this.meal.targets);

    // The meal exactly as the user is looking at it right now, kept so Undo can put it back
    // (#4971). Taken BEFORE the recipe split below, not just before the tuning: splitting a
    // recipe into its parts saves the meal on its own, so an Undo that only reversed the tuning
    // would leave the user with a meal they never asked for and no way back to the one they had.
    const mealBeforeTuning: AutoTuneMealSnapshot = {
      ingredients: deepClone(this.meal.ingredients),
      basedOnFoodId: this.meal.basedOnFoodId,
      autoPlanned: this.meal.autoPlanned,
    };

    // Whether the split below actually CHANGED the meal — not merely whether it ran. It can run
    // and change nothing, because it skips every recipe whose parts come back empty and every one
    // whose food cannot be read. That distinction is what decides whether the "cannot tune" dead
    // end has anything to offer Undo for: asking somebody to keep or undo a change that never
    // happened is a question with no meaning.
    let recipesWereSplit = false;

    // if all real ingredients are recipes, prompt to split (tuner needs variety)
    // if there are non-recipe ingredients alongside, recipes are tunable as whole units
    const hasNonRecipeIngredient = this.meal.ingredients.some(ingredient =>
      isIngredientReal(ingredient) && !isRecipeById(ingredient.foodId)
    );
    const onlyRecipes = !hasNonRecipeIngredient && this.meal.ingredients.some(ingredient =>
      isIngredientReal(ingredient) && isRecipeById(ingredient.foodId)
    );

    if (onlyRecipes) {
      const shouldSplit = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.editMeal.alerts.splitRecipesTitle(),
        message: LL.editMeal.alerts.splitRecipesMessage(),
        buttons: [
          { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
          { text: LL.editMeal.alerts.splitAndTune(), role: AlertButtonRole.Primary, result: true },
        ],
      });

      if (!shouldSplit) return;

      const splitOutcome = await this.splitRecipesAsync();

      // The split could not be written. `updateMealAsync` has already shown the user the
      // food-load alert, so there is nothing left to say — and going on to tune against a food
      // store that just refused to answer would only produce a second message contradicting the
      // first. Stop here instead (#5297).
      if (splitOutcome === RecipeSplitOutcome.SaveFailed) return;

      recipesWereSplit = splitOutcome === RecipeSplitOutcome.Split;
    }

    // The alert and the split above are both long enough for the user to walk away from — the
    // split reads every recipe's food and saves the meal. `runAutoTuneAsync` checks again after
    // its own food read; see `abandonAutoTuneIfUserLeft` for why neither check tries to put a
    // saved split back.
    if (this.abandonAutoTuneIfUserLeft(recipesWereSplit)) return;

    await this.runAutoTuneAsync(mealBeforeTuning, recipesWereSplit, tuningTargets);
  }

  /**
   * Replace every recipe in the meal with the foods it is made of, and save the meal that way.
   *
   * Answers what that ACHIEVED, not merely that it ran — see `RecipeSplitOutcome`. The save is
   * what makes the split real: `updateMealAsync` does not throw when a food cannot be read, it
   * opens the food-load alert and answers `false` without writing. Reporting a split that never
   * reached the database left the caller free to promise the user a meal it could then not put
   * back (#5297).
   */
  private async splitRecipesAsync(): Promise<RecipeSplitOutcome> {
    let anyRecipeSplit = false;

    // iterate in reverse to preserve indices when splicing
    for (let i = this.meal.ingredients.length - 1; i >= 0; i--) {
      const ingredient = this.meal.ingredients[i];
      if (!isIngredientReal(ingredient) || !isRecipeById(ingredient.foodId)) {
        continue;
      }

      try {
        const food = await this.foodDb.getFoodByIdAsync(ingredient.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
        if (!food || !isRecipe(food)) {
          continue;
        }

        // pass the weight of the entry being split, so the sub-ingredients come back at the
        // logged portion. this used to be scaled here by hand; the service now does it for every
        // caller, because having two copies of this arithmetic is how #4427 happened — the other
        // three split paths never got one.
        const { ingredients: subIngredients } = await this.foodDb.getSubIngredientsAsync(
          food,
          { portionWeight_g: ingredient.weight_g },
        );

        // if the split produced nothing, keep the recipe entry rather than removing it and
        // putting nothing back — that would delete food the user logged (#4427). Auto-Tune
        // reaches this loop without asking, so a silent deletion here is especially easy to
        // miss. the service has already reported why the split was empty.
        if (subIngredients.length === 0) {
          continue;
        }

        // replace recipe with its sub-ingredients
        this.meal.ingredients.splice(i, 1, ...subIngredients);
        anyRecipeSplit = true;
      } catch (err) {
        this.logging.trackAbnormalException(err);
      }
    }

    // Hand the ingredient list a NEW array. The splices above changed this array in place, and
    // the template binds `[ingredients]="meal.ingredients"` -- an unchanged reference means
    // ngOnChanges never fires there, so the list never reloads the display data for the rows the
    // split just added. Every other Auto-Tune path already assigns a fresh array (the tuner fill
    // and both undo routes); this one did not, and the rows it added were drawn against the data
    // of whichever row used to sit at their position. The path that reaches the user is the
    // tuner finding no solution: the alert offers Keep, and a kept split stays on screen with
    // nothing else reloading it.
    if (anyRecipeSplit) {
      this.meal.ingredients = [...this.meal.ingredients];
    }

    // save split result so it persists even if subsequent tuning fails
    const saved = await this.updateMealAsync();

    // A failed save outranks having changed the list, because the change the caller cares about
    // is the saved one. Note what this leaves behind: the split rows stay in `this.meal` and on
    // screen, unsaved. That is what every other edit on this page does when a food cannot be read
    // — see `updateMealAsync` — and the alternative is worse. Restoring the old rows here would
    // re-read the same food store that just failed, and the user would be shown the same alert
    // twice for one failure.
    if (!saved) return RecipeSplitOutcome.SaveFailed;

    this.cd.markForCheck();
    return anyRecipeSplit ? RecipeSplitOutcome.Split : RecipeSplitOutcome.NothingToSplit;
  }

  /**
   * Give up on Auto-Tune when the user has left the meal editor part-way through it, and answer
   * whether that happened (df-87dc6b282991).
   *
   * Named for what it DOES rather than as a plain question, because it also REPORTS. Calling it a
   * second time for one abandoned run would file the state twice and make the count unreadable,
   * so a third guard point wants a look at the two that exist rather than another call.
   *
   * Auto-Tune waits on several things the user can outlast — the "Split and tune?" alert, the
   * recipe split, which reads every recipe's food and saves the meal, and the tuner's own food
   * read after it. Going on past any of those once the page has gone rewrites and saves a meal
   * nobody is looking at, and every message Auto-Tune ends with then opens over whatever screen
   * the user moved on to. The Undo those messages offer would not work either:
   * `undoAutoTuneAsync` refuses on a destroyed view.
   *
   * A recipe split that was ALREADY SAVED is left split rather than put back. Putting it back
   * means writing and saving `this.meal` from a page that has gone, which is the thing being
   * prevented. So the user comes back to a meal whose recipes are split but not tuned, with
   * nothing telling them.
   *
   * That is a `trackSilentProblem` rather than the `trackSilentInfo` used by
   * `undoAutoTuneAsync`'s own page-left report a few methods below. The two look alike and are
   * not: there, the press arrived after everything had finished and nothing is wrong. Here the
   * user is left holding a change they only agreed to as half of something else, nothing repairs
   * it, and how often it happens is what decides whether this trade stays acceptable — which is
   * the tier's own test, "is this condition still unresolved?".
   *
   * Note the page also counts as gone while it is merely COVERED by a child page, so opening an
   * ingredient or a food search mid-tune abandons the tuning too. That is the same trade the
   * check after the day's-targets read in `handleAutoTune` already makes on this path.
   */
  private abandonAutoTuneIfUserLeft(recipesWereSplit: boolean): boolean {
    if (!this.viewDestroyed) return false;

    // if a split was already saved, record the half-done state the user will come back to
    if (recipesWereSplit) {
      this.logging.trackSilentProblem('AutoTuneAbandonedAfterPageLeft', { mealId: this.meal?._id });
    }

    return true;
  }

  private async runAutoTuneAsync(
    mealBeforeTuning: AutoTuneMealSnapshot,
    recipesWereSplit: boolean,
    tuningTargets: NutrientTargets,
  ) {
    // capture match before tuning to compare afterwards
    const matchBeforeTuning = calculateNutrientMatch_pc(this.meal.nutrients, tuningTargets);

    let tuningSource: MealTunerSource = {
      _id: this.tuningSource ? this.tuningSource._id : this.meal._id,
      ingredients: [],
    }
    for (let ingredient of this.meal.ingredients) {
      tuningSource.ingredients.push(ingredient);
    }
    let tuningParams = await this.foodDb.prepareTuningParamsFoodsAsync([tuningSource]);

    // The food read above is the last await before the meal is rewritten, so this one check
    // covers everything below it: the tuner-crash report, the cannot-tune alert, the no-gain bar
    // and the write-and-tell path all follow it without another await of their own.
    if (this.abandonAutoTuneIfUserLeft(recipesWereSplit)) return;

    tuningParams.targets = tuningTargets;

    let tuner = new MealTuner();
    let results = tuner.solve(tuningParams);

    // The tuner crashed. It catches everything it throws and hands the message back here instead,
    // which means an empty answer alone cannot tell a crash from the honest "no combination of
    // these foods fits" below. Read only the empty list, and our own arithmetic failing came out
    // as advice to put more variety in THEIR meal — a claim about the user's food we had no basis
    // for, and the only trace the crash left anywhere (#5297).
    //
    // Asked through `outcome` rather than by comparing a message against '': a failed solve carries no
    // meal list at all, so this check is what makes `results.meals` below readable in the first
    // place, and skipping it does not compile.
    if (results.outcome === MealTunerOutcome.Failed) {
      // Report before anything else runs, so the crash reaches Sentry whatever happens next. The
      // user is told something went wrong on our side; the wording deliberately says nothing
      // about their ingredients, and does not promise the meal is unchanged — putting the split
      // back can itself fail, and it says so in its own words when it does.
      this.logging.trackAbnormalError(
        `Auto-Tune: MealTuner.solve failed: ${results.error}`,
        LL.editMeal.errors.autoTuneFailed(),
      );

      // The user agreed to "Split and tune" as one action. The tuning half never happened, and
      // not through any fault of theirs, so the split goes back rather than leaving them with a
      // meal they never assembled and no way out of it (#4971). This is not the dead end below:
      // there the tuner gave a true answer the user can act on, so keeping the split for a
      // second attempt is worth offering them.
      if (recipesWereSplit) await this.undoAutoTuneAsync(mealBeforeTuning);
      return;
    }

    if (results.meals.length === 0) {
      // locked or hand-typed rows are something the user can change; otherwise the meal needs
      // different food. the rule is shared with the portion screen's Split & Auto-Tune.
      const cannotTuneReason = classifyCannotTune(this.meal.ingredients);

      // Nothing was tuned, so there is usually nothing to take back and the alert keeps its
      // single OK. The exception is a meal whose recipes were just split apart to make the
      // tuning possible: that change is already saved, and without an Undo here the user is
      // left with a meal they never assembled and no way back to the one they had (#4971).
      void this.showCannotTuneAlertAsync(cannotTuneReason, recipesWereSplit ? mealBeforeTuning : undefined);
      return;
    }
    // What the tuner is PROPOSING, measured before anything is applied. The tuner scores its own
    // answer against the very targets it was given, which are the same ones the verdict below
    // uses, so the two numbers are directly comparable.
    const proposedMatch = results.meals[0].precision_pc;

    // A meal that is already on target and that the tuner cannot MEANINGFULLY improve is left
    // exactly as it is. Rewriting every row to arrive at the same place is churn the user did not
    // ask for: it changes the numbers beside their food, and it leaves them holding an Undo for a
    // change that gained nothing. Saying so and changing nothing is the honest answer.
    //
    // A recipe split may already have been saved on the way here. Since the promise is that the
    // meal is untouched, that is put back too — the user agreed to the split in order to tune,
    // and there turned out to be nothing to tune.
    const cannotBeImproved = matchBeforeTuning !== undefined
      && matchBeforeTuning >= ON_TARGET_MATCH_PC
      && proposedMatch !== undefined
      && proposedMatch - matchBeforeTuning < MEANINGFUL_MATCH_GAIN_PC;

    if (cannotBeImproved) {
      // Putting the split back can fail, and it says so itself when it does — with a warning
      // toast, or with the food-load alert `updateMealAsync` opens. Claiming the meal is
      // untouched on top of that would be two contradictory messages about the same meal, so
      // when the restore did not happen this says nothing further.
      const mealIsUntouched = !recipesWereSplit || await this.undoAutoTuneAsync(mealBeforeTuning);
      if (this.viewDestroyed || !mealIsUntouched) return;

      this.toastCtrl.showToast({
        message: LL.editMeal.toasts.mealAlreadyOptimal(),
        type: ToastType.SuccessLight,
        // No Undo, because nothing was changed to undo. That leaves the toast with no action
        // row, so its way out is the corner close mark and its own timer.
        ...AUTO_TUNE_TOAST_PRESENTATION,
      });
      return;
    }

    // Using OnPush change detection. Hand over the exact rows that went into the tuner: the
    // meal is rebuilt against them by position, so that every row the user logged comes back
    // out -- hand-typed rows and two helpings of one food included (#4535).
    await this.foodDb.fillMealFromTunerAsync(this.meal, results.meals[0], tuningSource.ingredients);
    await this.updateMealAsync();
    // Note: don't call updateNutrientsAsync explicitly - ngOnChanges will trigger it
    // when Angular detects the ingredients array changed, avoiding double calculation
    this.cd.markForCheck();

    // The user can leave during the two writes above, and by then the tuned meal is saved — the
    // work they asked for is done and there is nothing half-finished to report. What must not
    // happen is the message: the bar or the alert below would open over whatever screen they
    // moved on to, offering an Undo that `undoAutoTuneAsync` refuses on a destroyed view. A bar
    // that appeared while they were still HERE and then outlives the page is a different thing
    // and is deliberate — see `undoAutoTuneAsync`.
    if (this.viewDestroyed) return;

    // show feedback based on tuning results
    const matchAfterTuning = calculateNutrientMatch_pc(this.meal.nutrients, tuningTargets);

    void this.showAutoTuneResultAsync(matchBeforeTuning, matchAfterTuning, mealBeforeTuning);
  }

  /**
   * Say what Auto-Tune did, and offer to take it back (#4971).
   *
   * The outcomes, and which one the user gets, are the point of this method (owner decision,
   * 2026-09-04, extended by #5297):
   *
   * - **The meal hit its targets.** A bar at the bottom that closes itself after a few seconds.
   *   There is nothing for the user to decide, so nothing should interrupt them. Its second
   *   button is the ordinary "Dismiss", because closing the bar early is all it does.
   * - **The meal still misses its targets.** An alert, because this one IS a decision: the meal
   *   the user is left with is not the meal they asked for, and the alert explains why their
   *   ingredients cannot reach the targets. Its buttons are the two real choices — "Keep" the
   *   tuned meal, or "Undo" back to the one they had.
   * - **The match could not be measured at all.** A bar again, saying only that the meal was
   *   tuned. This case used to be folded in with "misses its targets", which told the user their
   *   ingredients could not match the targets — a claim with nothing behind it, since the whole
   *   point is that nothing could be measured (#5297).
   *
   * The alert is NOT the pop-up #4971 set out to remove. That one had a single OK: it interrupted
   * the user to tell them something they could neither act on nor argue with. This one interrupts
   * them to ask a question they alone can answer.
   */
  private async showAutoTuneResultAsync(
    matchBeforeTuning: number | undefined,
    matchAfterTuning: number | undefined,
    mealBeforeTuning: AutoTuneMealSnapshot,
  ) {
    const isOnTarget = matchAfterTuning !== undefined && matchAfterTuning >= ON_TARGET_MATCH_PC;
    const wasOnTarget = matchBeforeTuning !== undefined && matchBeforeTuning >= ON_TARGET_MATCH_PC;
    // the tuning is what got the meal there, rather than it having been there all along
    const reachedTarget = isOnTarget && !wasOnTarget;

    if (matchAfterTuning !== undefined && !isOnTarget) {
      const shouldUndo = await this.alertCtrl.showAlertAsync<boolean>({
        title: LL.editMeal.alerts.tuneSuccessTitle(),
        messageMarkdown: LL.editMeal.alerts.tuneSuccessMessage(),
        // Keep is the Primary button, which is the one the alert fills in and draws the eye to.
        // Keeping the tuned meal is the ordinary outcome; Undo is the exception, so Undo must not
        // be the button that looks like what the user is meant to press.
        buttons: [
          { text: LL.common.buttons.keep(), role: AlertButtonRole.Primary, result: false },
          { text: LL.common.buttons.undo(), role: AlertButtonRole.Cancel, result: true },
        ],
        // What closing the alert without answering means — the X, the backdrop, the Back button.
        // Stated rather than left out: with no value here the alert resolves to `undefined`, and
        // this code would then be relying on `undefined` being falsy to avoid rewriting the meal.
        // It says "leave the meal alone" out loud instead.
        implicitCancelResult: false,
      });

      if (shouldUndo) await this.undoAutoTuneAsync(mealBeforeTuning);
      return;
    }

    this.toastCtrl.showToast({
      // "Now on target" is only true when the meal was NOT on target before. Saying it to
      // somebody whose meal was already on target claims the tuning achieved something it did
      // not, and reads as nonsense when they press the button twice. Every other case — a meal
      // that was and still is on target, one still short of it, one whose match could not be
      // measured — gets the plain statement that the meal was tuned.
      message: reachedTarget ? LL.editMeal.alerts.mealOnTarget() : LL.editMeal.toasts.mealTuned(),
      // Green for the one outcome that is genuinely good news; everything else is a statement of
      // where the meal stands, and the Undo beside it is the point of saying anything at all.
      type: reachedTarget ? ToastType.SuccessStrong : ToastType.SuccessLight,
      actionButtonText: LL.common.buttons.undo(),
      onAction: () => { void this.undoAutoTuneAsync(mealBeforeTuning); },
      ...AUTO_TUNE_TOAST_PRESENTATION,
    });
  }

  /**
   * Tell the user their meal cannot be tuned at all, and offer Undo when there is something to
   * undo — see the call site for when that is.
   *
   * The wording depends on which dead end it is, because they need different things from the
   * user. When rows are locked or hand-typed there is something they can go and change. When the
   * meal simply cannot be solved, the answer is different food, and telling them about locking
   * would be a red herring.
   */
  private async showCannotTuneAlertAsync(reason: CannotTuneReason, mealBeforeTuning?: AutoTuneMealSnapshot) {
    const hasUntunableRows = reason === CannotTuneReason.UntunableRows;
    const canUndo = mealBeforeTuning !== undefined;
    const shouldUndo = await this.alertCtrl.showAlertAsync<boolean>({
      title: hasUntunableRows
        ? LL.editMeal.alerts.cannotTuneLockedTitle()
        : LL.editMeal.alerts.cannotTuneTitle(),
      messageMarkdown: hasUntunableRows
        ? LL.editMeal.alerts.cannotTuneLockedMessage()
        : LL.editMeal.alerts.cannotTuneMessage(),
      // Keep is Primary and closing the alert leaves the meal alone, both for the reasons given in
      // `showAutoTuneResultAsync`.
      buttons: canUndo
        ? [
            { text: LL.common.buttons.keep(), role: AlertButtonRole.Primary, result: false },
            { text: LL.common.buttons.undo(), role: AlertButtonRole.Cancel, result: true },
          ]
        : [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: false }],
      implicitCancelResult: false,
    });

    // the second half is what tells the compiler the list is really there; `canUndo` is what
    // decided the button was offered at all, and the two cannot disagree.
    if (shouldUndo && mealBeforeTuning) await this.undoAutoTuneAsync(mealBeforeTuning);
  }

  /**
   * Put the meal back the way it was before Auto-Tune ran.
   *
   * Restores the whole snapshot in one go rather than trying to reverse the tuner step by step.
   * Auto-Tune may reweigh every row, drop rows and split a recipe into its parts, so the only
   * description of "before" that is certainly right is the copy taken at the start. That copy is
   * more than the ingredient list — see `AutoTuneMealSnapshot` for what else it must carry and
   * what went missing when it did not.
   *
   * Saving can fail, and the user pressed Undo and is owed either their old meal or an
   * explanation, so no failure passes in silence. The two kinds are answered in different
   * places on purpose. A food that cannot be READ is already `updateMealAsync`'s own case: it
   * opens the food-load-failed alert, returns without writing, and leaves the restored list on
   * screen unsaved — the same thing every other edit on this page does, so it is not repeated
   * here. Anything else travels out of `updateMealAsync`, and the catch below tells the user
   * and reports it to Sentry.
   */
  private async undoAutoTuneAsync(mealBeforeTuning: AutoTuneMealSnapshot): Promise<boolean> {
    // The bar keeps its Undo for a few seconds after the user has left the meal editor — toasts
    // are not dismissed on navigation — so a press can genuinely arrive with nothing left to
    // restore. Nothing is wrong, and there is no user left to tell, but the press is worth
    // counting: an Undo the user asked for and did not get looks the same from their side as one
    // that failed, and only telemetry can tell the two apart afterwards.
    if (this.viewDestroyed) {
      this.logging.trackSilentInfo('AutoTuneUndoAfterPageLeft', { mealId: this.meal?._id });
      return false;
    }

    try {
      this.meal.ingredients = deepClone(mealBeforeTuning.ingredients);

      // Put back the two fields the tuning path rewrites behind the ingredient list, or Undo
      // hands back the right food attached to a meal that has forgotten which recipe it came
      // from — see `AutoTuneMealSnapshot`. A field that was ABSENT before is deleted rather than
      // set to undefined, so the stored document comes back the shape it was rather than gaining
      // a key it never had.
      if (mealBeforeTuning.basedOnFoodId === undefined) {
        delete this.meal.basedOnFoodId;
      } else {
        this.meal.basedOnFoodId = mealBeforeTuning.basedOnFoodId;
      }
      if (mealBeforeTuning.autoPlanned === undefined) {
        delete this.meal.autoPlanned;
      } else {
        this.meal.autoPlanned = mealBeforeTuning.autoPlanned;
      }

      // Drop the cached tuning source before saving. `updateMealAsync` deletes `basedOnFoodId`
      // whenever the cached source's food set no longer matches the meal's — and the cached
      // source still describes the TUNED meal at this point, so leaving it in place lets the
      // save undo the line above and lose the recipe again. `cacheTuningValuesAsync` below
      // rebuilds it from the field just restored, which is the only version that is true.
      this.tuningSource = undefined;

      // A food that cannot be read stops the save without throwing — `updateMealAsync` opens the
      // food-load alert itself and answers `false`. Reporting success here would leave the caller
      // free to add "nothing was changed" on top of that alert, which is the opposite of what
      // happened. Say nothing further ourselves: the user has already been told.
      //
      // Redraw before leaving, even though nothing was saved. The restored list is what
      // `this.meal` now holds and what every later edit on this page will act on, so a screen
      // still showing the tuned rows would have the user editing a list they cannot see. Skip
      // only `cacheTuningValuesAsync` below, which re-reads the food store that just failed.
      if (!await this.updateMealAsync()) {
        this.cd.markForCheck();
        return false;
      }
      if (this.viewDestroyed) return true;

      // The recipe name and instructions on this page are read from the tuning source, which is
      // resolved FROM `basedOnFoodId`. Having just put that field back, re-resolve it, or the
      // page keeps showing the meal as the plain list the tuner left behind.
      await this.cacheTuningValuesAsync();
      if (this.viewDestroyed) return true;

      this.cd.markForCheck();
      return true;
    } catch (err) {
      this.logging.trackAbnormalException(err);
      // Nothing was restored either way; the guard only decides whether there is anyone to tell.
      if (this.viewDestroyed) return false;
      this.toastCtrl.showToast({
        message: LL.editMeal.toasts.undoTuneFailed(),
        type: ToastType.WarningLight,
        duration: AUTO_TUNE_TOAST_DURATION_MS,
      });
      return false;
    }
  }

  handleFindFood() {
    this.telemetry.trackEvent('meal_find_food', {});
  }

  /**
   * The Nutrients link in the macro card's title: opens the full nutrient list for whatever
   * the card is showing -- the meal or the day, logged or left -- the same list the card's
   * own link opened before the link moved up into the title.
   */
  protected async openNutrientList() {
    await this.navCtrl.pushAsync(INutritionInfoPage, { local: { nutrients: this.activeDisplayNutrients } });
  }

  handleQuickMacros() {
    this.telemetry.trackEvent('meal_quick_macros', {});
  }

  /**
   * The "Add to this meal" block at the top of the page (#4969).
   *
   * The rows are BUILT in the order measured in Mixpanel on 26 August 2026, most-used route
   * first, and then reordered by what this particular user does with them -- the product
   * owner's rule for this screen is that prominence follows how often a route is used, and
   * one number measured across everybody is only an opening guess at that. The reordering
   * rules, and why the first two places are fixed, are in `add-route-usage.ts`.
   *
   * Two rows are only offered when the feature behind them is configured, exactly as they
   * are elsewhere in the app: Scan Label needs some scanner model to be selected, and Scan
   * Meal needs the meal-photo scanner switched on. A row that is not offered is not drawn.
   */
  protected get addToMealRows(): AddToMealRow[] {
    const rows: AddToMealRow[] = [];

    rows.push({
      id: 'addFoodsRow',
      icon: 'add',
      title: LL.ingredientSearch.buttons.addFoods(),
      shortTitle: LL.editMeal.addToMeal.short.addFoods(),
      description: LL.editMeal.addToMeal.addFoodsDescription(),
      emphasized: true,
      handler: () => this.ingredientsCard.handleFindFood(),
    });

    rows.push({
      id: 'scanBarcodeRow',
      icon: 'barcode',
      title: LL.editMeal.addToMeal.scanBarcode(),
      shortTitle: LL.editMeal.addToMeal.short.scanBarcode(),
      description: LL.editMeal.addToMeal.scanBarcodeDescription(),
      handler: () => this.handleScanBarcode(),
    });

    if (this.scanLabelEnabled) {
      rows.push({
        id: 'scanLabelRow',
        icon: 'document',
        title: LL.editMeal.addToMeal.scanLabel(),
        shortTitle: LL.editMeal.addToMeal.short.scanLabel(),
        description: LL.editMeal.addToMeal.scanLabelDescription(),
        handler: () => this.handleScanLabel(),
      });
    }

    rows.push({
      id: 'logMacrosManuallyRow',
      icon: 'edit',
      title: LL.ingredientSearch.buttons.logMacrosManually(),
      shortTitle: LL.editMeal.addToMeal.short.logMacrosManually(),
      description: LL.editMeal.addToMeal.logMacrosManuallyDescription(),
      handler: () => this.ingredientsCard.handleLogMacrosManually(),
    });

    rows.push({
      id: 'scanMealRow',
      icon: 'camera',
      title: LL.editMeal.buttons.scanMeal(),
      shortTitle: LL.editMeal.addToMeal.short.scanMeal(),
      description: LL.editMeal.addToMeal.scanMealDescription(),
      handler: () => this.handleMealPhotoScan(),
    });

    rows.push({
      id: 'autoSuggestionsRow',
      icon: 'wand',
      title: LL.ingredientSearch.buttons.autoSuggestions(),
      shortTitle: LL.editMeal.addToMeal.short.autoSuggestions(),
      description: LL.editMeal.addToMeal.autoSuggestionsDescription(),
      handler: () => this.handleAutoPlan(),
    });

    rows.push({
      id: 'copyFromDayRow',
      icon: 'copy',
      title: LL.editMeal.buttons.copyFrom(),
      shortTitle: LL.editMeal.addToMeal.short.copyFrom(),
      description: LL.editMeal.addToMeal.copyFromDescription(),
      handler: () => { void this.copyFromDay(undefined); },
    });

    // built above in the order measured across all users, reordered here by what THIS user
    // actually uses. Add Foods and Scan Barcode keep the first two places whatever the
    // scores say -- see add-route-usage.ts.
    return orderRoutesByUsage(rows, this.routeOrderSnapshot);
  }

  /**
   * Scan a packaged product's barcode into THIS meal. Log returns to the list of results.
   *
   * Through the Foods page, because the scanner only fills its search box and the user then
   * taps a result: the list is part of this route, so `keepOpenAfterSelect` keeps it open and
   * Log comes back to it. The chosen food reaches this meal through handleFoodPageResult below,
   * exactly as from an ordinary search. The route table is in
   * `design-docs/nutrition-logging-routes.md`, "Where Log lands afterwards".
   */
  handleScanBarcode() {
    this.telemetry.trackEvent('meal_find_food', {});
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      openBarcodeScanner: true,
      keepOpenAfterSelect: true,
      returnCallback: 'handleFoodPageResult',
      local: {
        returnContext: { source: 'addFood' },
      },
    });
  }

  /**
   * Photograph a nutrition panel into THIS meal. Log returns to the meal.
   *
   * Straight to the scanner, with this meal's errand, and NOT through the Foods page (#5377):
   * nothing is picked off a list here, the scan creates the food. The scanner hands the errand to
   * the food editor, which answers handleFoodPageResult below with the same result a list pick
   * gives -- that is what `errandAnswerShape` asks for.
   */
  handleScanLabel() {
    this.telemetry.trackEvent('meal_find_food', {});
    void this.navCtrl.pushAsync(ILabelScannerPage, {
      returnCallback: 'handleFoodPageResult',
      errandAnswerShape: ErrandAnswerShape.FoodsPageResult,
      local: {
        returnContext: { source: 'addFood' },
      },
    });
  }

  // Auto-Suggestions, Scan Meal and Copy From a Day used to live here as well. They now sit
  // in the "Add to this meal" block at the top of the page (#4969), so this menu keeps only
  // what the block does not offer -- a second, hidden copy of a route the page has just made
  // prominent would only be a place for the two to drift apart.
  get ingredientListMenuItems(): PopupMenuButton[] {
    const items: PopupMenuButton[] = [];

    items.push(
      {
        text: LL.ingredientSearch.buttons.clearMeal(),
        handler: async () => {
          const cleared = await this.mealActionsService.clearMealAsync(this.meal);
          if (cleared) {
            // refresh UI after clearing
            // meal was already saved by clearMealAsync
            await this.ingredientsCard.loadIngredientsAsync();
            await this.cacheTuningValuesAsync();
            this.updateTotalNutrientsHandler(this.meal.nutrients);
            this.cd.markForCheck();
          }
        },
      },
      {
        text: LL.editMeal.buttons.saveAsRecipe(),
        handler: async () => {
          await this.saveAsRecipeAsync();
        },
      },
    );

    return items;
  }

  async fillMealFromComplexFoodAsync(food: ComplexFood) {
    let ingredients: Ingredient[] = [];
    let basedOnFoodId: string | undefined;
    try {
      const result = await this.foodDb.getSubIngredientsAsync(food);
      ingredients = result.ingredients;
      basedOnFoodId = result.basedOnFoodId;
    } catch (err) {
      this.logging.trackAbnormalException(err);
    }
    // if meal has no ingredients yet, set basedOnFoodId to track source
    if (this.meal.ingredients.length == 0 && basedOnFoodId) {
      this.meal.basedOnFoodId = basedOnFoodId;
    }
    this.meal.ingredients = this.meal.ingredients.concat(ingredients);

    await this.updateMealAsync();
  }

  /**
   * Answer from the portion screen. Declared as the list's EARLY handler, so this runs before
   * that screen's closing transition and must not navigate — see `handleEditResult` on
   * `ingredient-list`, and `returnCallbackImmediate` on CsPage.
   */
  onIngredientEdited(context: IngredientEditReturnContext, result: EditIngredientResult) {
    this.ingredientsCard.handleEditResult(context, result);
  }

  // handler for FoodsPage returnCallback - delegates to ingredient-list
  /**
   * Answer from the Foods page — hand it to the ingredient list.
   *
   * Returns the list's promise rather than dropping it (#4967). The Foods page can now stay
   * OPEN while this runs, and it needs to know when the work has finished: a pick that ends
   * up opening the portion screen must leave the list refusing further taps, while one that
   * gives up part-way must let the user try again. Dropping the promise made both look
   * identical, and the list released its guard immediately on every pick.
   */
  async handleFoodPageResult(context: { source?: string }, result: FoodsPageResult) {
    if (!result) return;

    // delegate to ingredient-list
    if (context?.source === 'addFood' || context?.source === 'viewAllRecentMeals') {
      await this.ingredientsCard.handleFoodPageResult(context as { source: string }, result);
    }
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
  }
}
